/**
 * @module qbLinkage/handlers/fieldSetting
 * @description QB 联动管道内同步 `field_setting`（表格维度 URL 格式）
 *
 * 自定义字段改名时**主路径**在 model.updateCustomColumn → applyCustomColumnRenameOnQB，
 * 本 handler 在管道检出 fieldIdentityRenamed 时作为兜底（依赖 useQBCategoryPartials 的 freezeQBSnapshot）。
 *
 * - 表字段只改 column_display：displayOnly，不 migrate
 * - 自定义维度改 column_name：migrate sub-key，patch 用 buildRecordSubKeyPatch 以删除旧 key
 *
 * @see utils/customColumnConfigRename.ts
 * @see hooks/dva/useQueryColumnLists.ts
 */

import type { IQBLinkageHandler } from '../types';
import {
  hasNonEmptyRecordInLayers,
  patchBoth,
  pruneRecordKeys,
  fieldSettingKey,
  isCustomQBField,
  migrateFieldSettingIdentity,
} from './shared';
import { buildRecordSubKeyPatch } from '../../../../utils/fieldSettingRename';

/** 维/指从 QB 移除时，删除对应 field_setting 条目 */
export const fieldSettingPruneHandler: IQBLinkageHandler = {
  id: 'fieldSetting-prune',
  phase: 'prune',
  keys: ['field_setting'],
  subscribe: ['dimensionRemoved', 'metricRemoved'],
  apply(changeSet, ctx) {
    const removed = [
      ...changeSet.removedDimensions,
      ...changeSet.removedMetrics,
    ];
    if (!removed.length) {
      return;
    }
    if (!hasNonEmptyRecordInLayers(ctx.base, ctx.draft, 'field_setting')) {
      return;
    }
    const drop = new Set(removed.map((f) => fieldSettingKey(f)));
    const baseNext = pruneRecordKeys(
      ctx.base.field_setting as Record<string, unknown> | undefined,
      (k) => drop.has(k),
    );
    const draftNext = pruneRecordKeys(
      ctx.draft.field_setting as Record<string, unknown> | undefined,
      (k) => drop.has(k),
    );
    const baseLayerPatch = buildRecordSubKeyPatch(
      ctx.base.field_setting as Record<string, unknown> | undefined,
      baseNext,
    );
    const draftLayerPatch = buildRecordSubKeyPatch(
      ctx.draft.field_setting as Record<string, unknown> | undefined,
      draftNext,
    );
    return patchBoth(
      baseLayerPatch ? { field_setting: baseLayerPatch } : undefined,
      draftLayerPatch ? { field_setting: draftLayerPatch } : undefined,
    );
  },
};

/** 自定义维度 column_name 变更时，迁移 field_setting 的 sub-key */
export const fieldSettingMigrateHandler: IQBLinkageHandler = {
  id: 'fieldSetting-migrate',
  phase: 'migrate',
  keys: ['field_setting'],
  subscribe: ['fieldIdentityRenamed'],
  apply(changeSet, ctx) {
    if (!hasNonEmptyRecordInLayers(ctx.base, ctx.draft, 'field_setting')) {
      return;
    }
    const renames = changeSet.renamed
      .filter(
        (r) =>
          !r.displayOnly &&
          r.field.builder_type === 'dimension' &&
          isCustomQBField(r.field),
      )
      .map((r) => ({
        prev: `${r.field.table_code || 't0'}_${r.prevColumnName}`,
        next: fieldSettingKey(r.field),
        snap: r.field,
      }));
    if (!renames.length) {
      return;
    }
    const baseNext = migrateFieldSettingIdentity(
      ctx.base.field_setting as Record<string, unknown> | undefined,
      renames,
    );
    const draftNext = migrateFieldSettingIdentity(
      ctx.draft.field_setting as Record<string, unknown> | undefined,
      renames,
    );
    const baseLayerPatch = buildRecordSubKeyPatch(
      ctx.base.field_setting as Record<string, unknown> | undefined,
      baseNext,
    );
    const draftLayerPatch = buildRecordSubKeyPatch(
      ctx.draft.field_setting as Record<string, unknown> | undefined,
      draftNext,
    );
    return patchBoth(
      baseLayerPatch ? { field_setting: baseLayerPatch } : undefined,
      draftLayerPatch ? { field_setting: draftLayerPatch } : undefined,
    );
  },
};

/** @deprecated 使用 fieldSettingPruneHandler + fieldSettingMigrateHandler */
export const fieldSettingHandler = fieldSettingPruneHandler;
